import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertCircle, GraduationCap, Search, X } from 'lucide-react';
import { api } from '../services/api';

/**
 * Frais d'examens nationaux encaissés — carré du portefeuille.
 *
 * Ces versements n'apparaissaient nulle part dans la caisse : la route de
 * paiement vérifiait que la caisse était ouverte puis n'y écrivait rien.
 * Chaque versement produit désormais une ligne dans `portefeuille_transactions`
 * rattachée par `paiement_examen_id` ; ce tableau lit cette clé, jamais le
 * libellé de la transaction.
 *
 * Une ligne = UN versement, pas le cumul dû par le candidat : c'est ce qui
 * permet d'attribuer chaque encaissement à la gestionnaire qui l'a reçu.
 */
const TableauFraisExamens = ({
  anneeActive,
  selectedDate,
  filterPeriode,
  bornesMois = null,
  formatDateForAPI,
  formatDateForDisplay,
  formatMontant
}) => {
  const [lignes, setLignes] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalMontant, setTotalMontant] = useState(0);
  const [page, setPage] = useState(1);
  const [nbPages, setNbPages] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [premierChargement, setPremierChargement] = useState(true);

  const chargementEnCours = useRef(false);
  const derniersParams = useRef(null);
  const PAR_PAGE = 20;

  const charger = useCallback(async (force = false) => {
    if (!anneeActive) return;
    if (chargementEnCours.current && !force) return;

    const params = {
      page,
      limit: PAR_PAGE,
      annee_scolaire_id: anneeActive.id,
      // La clé de rattachement, pas le libellé : un frais d'examen reste un
      // frais d'examen même si quelqu'un renomme la transaction.
      type_encaissement: 'examen'
    };

    // Les bornes, quel que soit le mode. Ne traiter que le jour laissait le
    // mode mois sans aucune date -- et la route /encaissements ne connait pas
    // la notion de periode : sans bornes, elle rend toute l'annee.
    if (filterPeriode === 'jour') {
      const jour = formatDateForAPI(selectedDate);
      params.date_debut = jour;
      params.date_fin = jour;
    } else if (bornesMois?.dateDebut && bornesMois?.dateFin) {
      params.date_debut = bornesMois.dateDebut;
      params.date_fin = bornesMois.dateFin;
    }
    if (recherche.trim().length >= 2) params.search = recherche.trim();

    const signature = JSON.stringify(params);
    if (derniersParams.current === signature && !force) return;
    derniersParams.current = signature;
    chargementEnCours.current = true;

    try {
      const reponse = await api.portefeuille.getEncaissements(params);
      if (reponse.success) {
        setLignes(reponse.data || []);
        setTotal(reponse.pagination?.total || 0);
        setNbPages(reponse.pagination?.total_pages || 1);
        setTotalMontant(
          (reponse.data || []).reduce((somme, l) => somme + Number(l.montant || 0), 0)
        );
        setPremierChargement(false);
      }
    } catch (erreur) {
      console.error('Erreur chargement des frais d\'examens:', erreur);
    } finally {
      chargementEnCours.current = false;
    }
  }, [anneeActive, page, filterPeriode, selectedDate, bornesMois, formatDateForAPI, recherche]);

  useEffect(() => {
    if (anneeActive) charger(true);
  }, [anneeActive, selectedDate, filterPeriode, bornesMois]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!premierChargement) charger();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // La recherche attend que la frappe se pose.
  useEffect(() => {
    if (premierChargement) return;
    const minuteur = setTimeout(() => {
      setPage(1);
      charger(true);
    }, 400);
    return () => clearTimeout(minuteur);
  }, [recherche]); // eslint-disable-line react-hooks/exhaustive-deps

  const nomCandidat = (l) => {
    if (l.eleve_nom || l.eleve_prenom) {
      return `${l.eleve_prenom || ''} ${l.eleve_nom || ''}`.trim();
    }
    if (l.candidat_libre_nom || l.candidat_libre_prenom) {
      return `${l.candidat_libre_prenom || ''} ${l.candidat_libre_nom || ''}`.trim();
    }
    return 'Candidat inconnu';
  };

  return (
    <div className="px-6 pb-6 pt-2 space-y-4">
      {/* Recherche */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher un candidat…"
          className="h-9 pl-9 pr-9"
        />
        {recherche && (
          <button
            type="button"
            onClick={() => setRecherche('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {premierChargement ? (
        <div className="flex h-40 items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#246990] border-t-transparent" />
            <span className="font-medium text-[#246990]">Chargement…</span>
          </div>
        </div>
      ) : lignes.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 py-10 text-center">
          <GraduationCap size={28} className="text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">Aucun frais d'examen encaissé</p>
          <p className="text-[11px] text-gray-500">
            {filterPeriode === 'jour'
              ? `Aucun versement le ${formatDateForDisplay(selectedDate)}.`
              : 'Aucun versement sur la période.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              {/* Meme forme que l'en-tete des encaissements — teinte et souligne —
                  mais dans le teal du bloc plutot que son vert. */}
              <thead className="border-b-2 border-[#319190]/30 bg-[#319190]/10">
                <tr className="text-left text-xs font-semibold uppercase text-[#2a7574]">
                  <th className="px-4 py-3">Candidat</th>
                  <th className="px-4 py-3">Examen</th>
                  <th className="px-4 py-3">Classe</th>
                  <th className="px-4 py-3">Famille</th>
                  <th className="px-4 py-3 text-right">Versé</th>
                  <th className="px-4 py-3 text-right">Reste dû</th>
                  <th className="px-4 py-3">Date &amp; heure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((l) => {
                  const attendu = Number(l.examen_montant_attendu || 0);
                  const cumul = Number(l.examen_montant_paye || 0);
                  const reste = Math.max(0, Math.round(attendu - cumul));
                  return (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{nomCandidat(l)}</p>
                        {l.eleve_matricule ? (
                          <p className="text-xs text-gray-500">{l.eleve_matricule}</p>
                        ) : !l.eleve_id ? (
                          <span className="text-[10px] font-semibold uppercase text-[#319190]">
                            Candidat libre
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex h-6 items-center rounded-md bg-[#eef7fb] px-2 text-[10px] font-semibold text-[#246990]">
                          {l.examen_type || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{l.classe_nom || '—'}</td>
                      <td className="px-4 py-3">
                        {l.famille_nom
                          ? <span className="text-sm text-[#246990]">{l.famille_nom}</span>
                          : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900">
                        {formatMontant(l.montant)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {reste > 0 ? (
                          <span className="font-semibold text-orange-600">{formatMontant(reste)}</span>
                        ) : (
                          <span className="text-gray-400">Soldé</span>
                        )}
                      </td>
                      {/* Date ET heure : l'heure compte au rapprochement de caisse. */}
                      <td className="px-4 py-3">
                        {l.date_transaction ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(l.date_transaction).toLocaleDateString('fr-FR')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(l.date_transaction).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-gray-500">
              {total} versement{total > 1 ? 's' : ''}
              {nbPages > 1 && ` — page ${page} sur ${nbPages}`}
              {' · '}
              <span className="font-semibold text-gray-700 tabular-nums">
                {formatMontant(totalMontant)}
              </span>
              {nbPages > 1 && ' sur cette page'}
            </p>

            {nbPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= nbPages}
                  onClick={() => setPage((p) => Math.min(nbPages, p + 1))}
                >
                  Suivant
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Le total affiché ne porte que sur la page courante : le dire évite de
          lire ce chiffre comme le total de la période. */}
      {nbPages > 1 && (
        <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-500">
          <AlertCircle size={13} className="mt-px flex-shrink-0 text-gray-400" />
          Le montant indiqué est celui des versements affichés sur cette page.
        </p>
      )}
    </div>
  );
};

export default TableauFraisExamens;
